import { useState, useRef, useEffect } from 'react';
import type { TaskRow } from '../api/types';
import { tasksApi } from '../api/client';
import { haptic } from '../hooks/useHaptics';

interface Props {
  listId: string;
  onCreated: (task: TaskRow) => void;
}

export function QuickAddTask({ listId, onCreated }: Props) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  const submit = async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    try {
      const row = await tasksApi.create({ list_id: listId, title: title.trim() });
      haptic('success');
      onCreated(row);
      setTitle('');
      inputRef.current?.focus();
    } catch (err) {
      haptic('error');
      console.error(err);
      alert(`创建失败: ${(err as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!adding) {
    return (
      <button
        onClick={() => setAdding(true)}
        className="fx-tap flex w-full items-center gap-2 rounded px-2 py-2 text-sm text-gray-400 transition hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-gray-800"
      >
        <span>+</span>
        <span>添加任务</span>
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded border border-blue-300 bg-white p-2 dark:bg-gray-800">
      <input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') {
            setAdding(false);
            setTitle('');
          }
        }}
        onBlur={() => {
          if (!title.trim()) setAdding(false);
        }}
        placeholder="任务名 + 回车"
        disabled={submitting}
        className="w-full rounded border-0 bg-transparent px-1 py-1 text-sm outline-none"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={submit}
          disabled={!title.trim() || submitting}
          className="fx-tap rounded bg-blue-500 px-3 py-1 text-xs text-white hover:bg-blue-600 disabled:opacity-50"
        >
          添加
        </button>
      </div>
    </div>
  );
}